import { faComment } from "@fortawesome/free-regular-svg-icons";
import { faEllipsis } from "@fortawesome/free-solid-svg-icons";
import {
  faArrowUpFromBracket,
  faBookmark,
  faChartSimple,
  faDesktop,
  faHeart,
  faMessage,
  faMobilePhone,
  faPaperPlaneTop,
  faPhone,
  faPlus,
  faRetweet,
  faShare,
  faThumbsUp,
  faWaveformLines,
  IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import { faBadgeCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { format, isToday, sub } from "date-fns";
import React, { useState } from "react";
import Moment from "react-moment";
import { useMutation } from "react-query";
import { useParams } from "react-router-dom";
import { Button, Divider, Header, Icon, Menu, Segment, Image as SemanticImage } from "semantic-ui-react";
import { StyledReactMarkdown } from "../../../components/StyledReactMarkdown";
import { useClient } from "../../../hooks/useClient";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import useNetworkHeaders from "../../../hooks/useNetworkHeaders";
import { postVoiceNotificationGeneration } from "../../../queries/notify";
import { useNotifySettings } from "../hooks/useNotifySettings";
import useNotifySocialMediaPreview from "../hooks/useNotifySocialMediaImagePreview";
import TwoWayCommunicationButton from "./public_view/TwoWayCommunicationButton";
import { stripNonGSM7Characters } from "../routes/notify/notifications/components/NotifyNotificationsPreview";

type ActiveItemType = (typeof ActiveItemTypes)[keyof typeof ActiveItemTypes];
const ActiveItemTypes = {
  Mobile: "mobile",
  Desktop: "desktop",
  Sms: "sms",
  Voice: "voice",
  Twitter: "twitter",
  Instagram: "instagram",
  Facebook: "facebook",
} as const;
export const CommunicationsPublicViewPreview = ({
  content,
  tagline,
  headline,
  voiceContent,
  publishedAt,
  classificationId,
}: {
  content: string;
  tagline: string;
  headline: string;
  voiceContent?: string;
  publishedAt?: Date | null;
  classificationId?: string | null;
}) => {
  const networkHeaders = useNetworkHeaders();
  const { site } = useParams<{ site: string }>();

  const { isEnabled: isNewTwitterEnabled } = useFeatureFlag("notify.twitter_integration");
  const { isEnabled: isNewInstagramEnabled } = useFeatureFlag("notify.instagram_integration");
  const { isEnabled: isNewFacebookEnabled } = useFeatureFlag("notify.facebook_integration");

  const [activeItem, setActiveItem] = useState<ActiveItemType>(ActiveItemTypes.Mobile);
  const [previouslyGeneratedVoiceContent, setPreviouslyGeneratedVoiceContent] = useState<string | undefined>();

  const voiceContentHasChanged = voiceContent?.trim() !== previouslyGeneratedVoiceContent?.trim();

  const client = useClient(site);
  const cityName = client.query.data?.name;
  const citySeal = client.query.data?.seal ?? "";

  const { notifySettings, isConfigured: isNotifySettingsConfigured } = useNotifySettings({
    enabled: activeItem === ActiveItemTypes.Sms,
  });

  const [generateVoicePreviewMutation, { isLoading: isGenerateVoicePreviewMutationLoading, data: voicePreview }] =
    useMutation(postVoiceNotificationGeneration(networkHeaders));

  const smsNotificationPreview = () => {
    const maxLength = 160;

    if (!notifySettings) return "Loading...";

    const { smsPreviewNotificationMessage } = notifySettings;

    const body = `${stripNonGSM7Characters(headline)}\n${stripNonGSM7Characters(tagline)}`.trim();
    const bodyIsEmpty = body.length === 0;
    if (bodyIsEmpty) return smsPreviewNotificationMessage.replace("\n{{BODY}}", "");

    const lengthWithoutBody = smsPreviewNotificationMessage.replace("{{BODY}}", "").length;
    const maxBodyLength = maxLength - lengthWithoutBody;
    const shouldTrim = body.length > maxBodyLength;

    const trimmedBody = shouldTrim ? `${body.substring(0, maxBodyLength - 3)}...` : body;

    return smsPreviewNotificationMessage.replace("{{BODY}}", trimmedBody);
  };

  const smsTimestamp = (date?: Date | null) => {
    const actualDate = !!date ? date : new Date();
    const actualDateIsToday = isToday(actualDate);
    if (actualDateIsToday) return format(actualDate, "'Today' h:mm a");
    return format(actualDate, "E, LLL d 'at' h:mm a"); // e.g. Thu, Aug 15 at 11:55 AM
  };

  const { canvasRef: twitterPreviewRef } = useNotifySocialMediaPreview({ headline, tagline, content, citySeal });
  const { canvasRef: instagramPreviewRef } = useNotifySocialMediaPreview({ headline, tagline, content, citySeal });
  const { canvasRef: facebookPreviewRef } = useNotifySocialMediaPreview({ headline, tagline, content, citySeal });

  const twitterPreviewContent = createTwitterPostMessage(headline, tagline, content);
  return (
    <div className='tw-flex tw-flex-col tw-items-center'>
      <Menu borderless className='tw-mb-6 tw-flex tw-w-full tw-flex-col tw-shadow-none sm:tw-flex-row'>
        <Menu.Item header>Preview</Menu.Item>
        <Menu.Item
          name='mobile'
          active={activeItem === ActiveItemTypes.Mobile}
          onClick={() => setActiveItem(ActiveItemTypes.Mobile)}
        >
          <Icon>
            <FontAwesomeIcon icon={faMobilePhone} />
          </Icon>
          Mobile
        </Menu.Item>
        <Menu.Item
          name='desktop'
          active={activeItem === ActiveItemTypes.Desktop}
          onClick={() => setActiveItem(ActiveItemTypes.Desktop)}
        >
          <Icon>
            <FontAwesomeIcon icon={faDesktop} />
          </Icon>
          Desktop
        </Menu.Item>
        <Menu.Item
          disabled={!isNotifySettingsConfigured}
          name='sms'
          active={activeItem === ActiveItemTypes.Sms}
          onClick={() => setActiveItem(ActiveItemTypes.Sms)}
        >
          <Icon>
            <FontAwesomeIcon icon={faMessage} />
          </Icon>
          SMS
        </Menu.Item>
        <Menu.Item
          disabled={!isNotifySettingsConfigured}
          name='voice'
          active={activeItem === ActiveItemTypes.Voice}
          onClick={() => setActiveItem(ActiveItemTypes.Voice)}
        >
          <Icon>
            <FontAwesomeIcon icon={faPhone} />
          </Icon>
          Voice
        </Menu.Item>
        {isNewFacebookEnabled && (
          <Menu.Item
            name='facebook'
            active={activeItem === ActiveItemTypes.Facebook}
            onClick={() => setActiveItem(ActiveItemTypes.Facebook)}
          >
            <Icon>
              <img src='/assets/integrations/facebook/logo.png' alt='Facebook' className='tw-h-4 tw-w-4' />
            </Icon>
            Facebook
          </Menu.Item>
        )}
        {isNewInstagramEnabled && (
          <Menu.Item
            name='instagram'
            active={activeItem === ActiveItemTypes.Instagram}
            onClick={() => setActiveItem(ActiveItemTypes.Instagram)}
          >
            <Icon>
              <img src='/assets/integrations/instagram/logo.png' alt='Instagram' className='tw-h-4 tw-w-4' />
            </Icon>
            Instagram
          </Menu.Item>
        )}
        {isNewTwitterEnabled && (
          <Menu.Item
            name='twitter'
            active={activeItem === ActiveItemTypes.Twitter}
            onClick={() => setActiveItem(ActiveItemTypes.Twitter)}
          >
            <Icon>
              <img src='/assets/integrations/twitter/logo.png' alt='Twitter' className='tw-h-4 tw-w-4' />
            </Icon>
            Twitter
          </Menu.Item>
        )}
      </Menu>

      <PreviewContainer className='tw-max-w-sm' visible={activeItem === ActiveItemTypes.Mobile}>
        <Header as='h3' className='tw-m-0 tw-text-lg tw-font-normal'>
          {cityName}
        </Header>
        <Header as='h1' className='tw-m-0 tw-mt-6 tw-text-2xl'>
          {headline}
        </Header>
        <Header as='h3' className='tw-mt- tw-m-0 tw-text-lg tw-font-normal'>
          {tagline}
          <Header.Subheader className='tw-mt-3'>
            {"Published:  "}
            <Moment format={"MMM D, YYYY @ h:mm A"}>{publishedAt ?? new Date()}</Moment>
          </Header.Subheader>
        </Header>
        <Divider className='-tw-mx-6 tw-my-6' />
        <StyledReactMarkdown className='tw-text-black'>{content}</StyledReactMarkdown>
        {!!classificationId && (
          <div className='tw-mt-6 tw-flex tw-justify-center'>
            <TwoWayCommunicationButton onClick={() => {}} />
          </div>
        )}
      </PreviewContainer>

      <PreviewContainer className='tw-max-w-3xl' visible={activeItem === ActiveItemTypes.Desktop}>
        <div className='tw-mb-6'>
          <Header as='h3' className='tw-font-normal'>
            <SemanticImage src={citySeal} size='massive' />
            {cityName}
          </Header>
        </div>
        <div>
          <Header as='h1' className='tw-mb-1'>
            <Header.Content>{headline}</Header.Content>
          </Header>
          <Header as='h3' className='tw-mt-0 tw-font-normal'>
            {tagline}
            <Header.Subheader className='tw-mt-3'>
              {"Published:  "}
              <Moment format={"MMM D, YYYY @ h:mm A"}>{publishedAt ?? new Date()}</Moment>
            </Header.Subheader>
          </Header>
          <Divider className='-tw-mx-6 tw-my-6' />
          <StyledReactMarkdown>{content}</StyledReactMarkdown>
          {!!classificationId && (
            <div className='tw-mt-6 tw-flex tw-justify-center'>
              <TwoWayCommunicationButton onClick={() => {}} />
            </div>
          )}
        </div>
      </PreviewContainer>

      <PreviewContainer className='tw-max-w-sm' visible={activeItem === ActiveItemTypes.Sms}>
        <SMSConversationPreview>
          <SMSConversationPreview.Fade />
          <SMSConversationPreview.SMSSenderMessage
            timestamp={smsTimestamp(sub(publishedAt ?? new Date(), { days: 1, hours: 3, minutes: 6, seconds: 10 }))}
          >
            {notifySettings?.smsPreviewInviteMessage ?? "Loading..."}
          </SMSConversationPreview.SMSSenderMessage>
          <SMSConversationPreview.SMSRecipientMessage>JOIN</SMSConversationPreview.SMSRecipientMessage>
          <SMSConversationPreview.SMSSenderMessage>
            {notifySettings?.smsPreviewWelcomeMessage ?? "Loading..."}
          </SMSConversationPreview.SMSSenderMessage>
          <SMSConversationPreview.SMSSenderMessage highlight timestamp={smsTimestamp(publishedAt)}>
            {smsNotificationPreview()}
          </SMSConversationPreview.SMSSenderMessage>
        </SMSConversationPreview>
      </PreviewContainer>

      <PreviewContainer className='tw-max-w-full' visible={activeItem === ActiveItemTypes.Voice}>
        <Button
          onClick={async () => {
            if (!voiceContent) return;
            setPreviouslyGeneratedVoiceContent(voiceContent);
            await generateVoicePreviewMutation({ voiceContent, isPreview: true });
          }}
          loading={isGenerateVoicePreviewMutationLoading}
          disabled={isGenerateVoicePreviewMutationLoading || !voiceContent || !voiceContentHasChanged}
          icon={<FontAwesomeIcon icon={faWaveformLines} className='tw-mr-2' />}
          content='Generate Preview'
        />
        {isGenerateVoicePreviewMutationLoading && (
          <>
            <Divider className='-tw-mx-6 tw-my-6' />
            <div>Generating... For long messages, this might take a while</div>
          </>
        )}
        {voicePreview && (
          <>
            <Divider className='-tw-mx-6 tw-my-6' />
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <audio controls src={voicePreview.url}>
              Your browser does not support the audio element.
              <a href={voicePreview.url} download>
                Download Audio
              </a>
            </audio>
            <div className='tw-mt-3 tw-whitespace-pre-wrap'>{voicePreview.transcript}</div>
          </>
        )}
      </PreviewContainer>

      <PreviewContainer className='tw-max-w-xl' visible={activeItem === ActiveItemTypes.Twitter}>
        <Segment basic className='tw-p-0'>
          <div className='tw-flex'>
            <Header className='tw-mr-2'>
              <SemanticImage className='tw-my-0 tw-mr-1' src={citySeal} size='small' />
            </Header>
            <div className='tw-my-0'>
              <Header className='tw-mb-0 tw-flex tw-justify-between'>
                <div>
                  <span className='tw-text-base'>{cityName}</span>
                  <FontAwesomeIcon icon={faBadgeCheck} className='tw-mx-1 tw-text-[#3796EF]' />
                  <span className='tw-text-base tw-font-normal'>@{cityName?.replaceAll(" ", "") ?? ""}</span>
                  <span className='tw-mx-1 tw-text-base tw-font-normal tw-text-[#6D6D6D]'>&#x2022;</span>
                  <span className='tw-text-base tw-font-normal tw-text-[#6D6D6D]'>May 24</span>
                </div>
                <FontAwesomeIcon icon={faEllipsis} className='tw-text-[#6D6D6D]' />
              </Header>

              <div className='tw-mb-1'>{twitterPreviewContent.header}</div>
              <div className='tw-mb-1'>{twitterPreviewContent.previewText}</div>
              <div className='tw-mb-1'>
                Read More: <span className='tw-text-blue'>{buildUrl(site)}</span>
              </div>

              <canvas
                width={1200}
                height={1200}
                className='tw-max-w-md  tw-rounded-xl tw-border tw-border-solid tw-border-black'
                ref={twitterPreviewRef}
              />
              <Segment basic className='tw-my-2 tw-flex tw-justify-between tw-p-0'>
                <TwitterIcon icon={faComment} />
                <TwitterIcon icon={faRetweet} />
                <TwitterIcon icon={faHeart} />
                <TwitterIcon icon={faChartSimple} />
                <div>
                  <FontAwesomeIcon className='tw-text-[#6D6D6D]' icon={faArrowUpFromBracket} />
                </div>
              </Segment>
            </div>
          </div>
        </Segment>
      </PreviewContainer>

      <Segment
        className={`tw-max-w-xl tw-border-black tw-p-0 ${activeItem === ActiveItemTypes.Instagram ? "" : "tw-hidden"}`}
      >
        <Segment basic className='tw-p-0'>
          <div className='tw-flex tw-justify-between tw-p-4 tw-pb-2'>
            <Header className='tw-mr-2'>
              <SemanticImage className='tw-my-0 tw-mr-1' src={citySeal} size='small' />
              <span className='tw-text-base'>{cityName}</span>
            </Header>
            <div>
              <Button className='tw-mr-4' size='small'>
                Follow
              </Button>
              <FontAwesomeIcon icon={faEllipsis} className='tw-text-[#6D6D6D]' />
            </div>
          </div>
          <canvas width={1200} height={1200} className='tw-max-w-[100%]' ref={instagramPreviewRef} />
          <div className='tw-border-[1px] tw-border-solid tw-border-[#CCCCCC]'></div>
          <div className='tw-px-4 tw-py-4'>
            <div className='tw-mb-2 tw-flex tw-justify-between'>
              <div className='tw-flex'>
                <FontAwesomeIcon className='tw-mr-5 tw-text-2xl' icon={faHeart} />
                <FontAwesomeIcon className='tw-mr-5 tw-text-2xl' flip={"horizontal"} icon={faComment} />
                <FontAwesomeIcon className='tw-text-2xl' icon={faPaperPlaneTop} />
              </div>
              <div>
                <FontAwesomeIcon className='tw-text-2xl' icon={faBookmark} />
              </div>
            </div>
            <div className='tw-mb-2 tw-font-semibold'>420 likes</div>
            <div>
              <div className='tw-flex'>
                <span>
                  <span className='tw-mr-1 tw-font-semibold'>{cityName}</span> {tagline}
                </span>
              </div>
              {formatInstagramContent(headline, tagline, content) && (
                <>
                  <br />
                  <div>{formatInstagramContent(headline, tagline, content)}</div>
                </>
              )}
            </div>
          </div>
        </Segment>
      </Segment>

      <PreviewContainer className='tw-max-w-xl' visible={activeItem === ActiveItemTypes.Facebook}>
        <Segment basic className='tw-m-0 tw-rounded-lg tw-p-0'>
          <div>
            <div className='tw-flex tw-justify-between'>
              <Header className='tw-mr-2'>
                <SemanticImage className='tw-my-0 tw-mr-1' src={citySeal} size='small' />
                <span className='tw-text-base'>{cityName}</span>
              </Header>
              <div>
                <FontAwesomeIcon icon={faEllipsis} className='tw-text-[#6D6D6D]' />
              </div>
            </div>
            {headline && (
              <>
                <div>{headline}</div>
                <br />
              </>
            )}
            {tagline.trim() && (
              <>
                <div>{tagline}</div>
                <br />
              </>
            )}
            {markdownToPlainText(content) && (
              <>
                <div>
                  {markdownToPlainText(content)
                    .split("\n")
                    .map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                </div>
                <br />
              </>
            )}
            <div>
              Read More: <span className='tw-text-blue'>{buildUrl(site)}</span>
            </div>
          </div>
          <canvas
            className='tw-mt-4 tw-max-w-[100%] tw-border-[1px] tw-border-solid tw-border-black'
            width={1200}
            height={1200}
            ref={facebookPreviewRef}
          />
          <div>
            <div className='tw-border-[1px] tw-border-solid tw-border-[#CCCCCC]'></div>
            <div className='tw-my-2 tw-flex tw-justify-around'>
              <span className='tw-text-[#9E9D9D]'>
                <FontAwesomeIcon className='tw-mr-1 tw-text-xl ' icon={faThumbsUp} />
                Like
              </span>
              <span className='tw-text-[#9E9D9D]'>
                <FontAwesomeIcon className='tw-mr-1 tw-text-xl' icon={faComment} />
                Comment
              </span>
              <span className='tw-text-[#9E9D9D]'>
                <FontAwesomeIcon className='tw-mr-1 tw-text-xl' icon={faShare} />
                Share
              </span>
            </div>
          </div>
        </Segment>
      </PreviewContainer>
    </div>
  );
};

function formatInstagramContent(headline: string, tagline: string, content: string): string {
  // Instagram's max length for a caption is 2200 characters - if the tagline or headline is
  // more than 2200 characters, we'll deal with it later...
  const maxLength = 2200 - headline?.length - tagline?.length;
  return formatMessage(markdownToPlainText(content), maxLength);
}

export function createTwitterPostMessage(
  headline: string | null,
  preview: string | null,
  content: string | null,
): { header: string; previewText: string } {
  const formattedContent = markdownToPlainText(content);
  const formattedPreview = formatPreview(preview);

  const header = formatMessage(headline ?? "", 230);
  const previewText = formatMessage(preview ? `${formattedPreview} ${formattedContent}` : "", 230 - header.length);

  return { header: header, previewText: previewText };
}

function buildUrl(site: string): string {
  return `${window.location.origin}/${site}`.slice(0, 50);
}

/**
 * Needed to copy some of the message generating logic over from the backend... I think
 * the only way to really share it would be to create a package for it...
 *
 * -- Peter
 */

export function markdownToPlainText(text: string | null): string {
  if (!text) return "";
  // Remove code blocks
  text = text.replace(/```[\s\S]*?```/g, "");
  // Remove inline code
  text = text.replace(/`([^`]*)`/g, "$1");
  // Remove images
  text = text.replace(/!\[.*?\]\(.*?\)/g, "");
  // Remove links but keep the link text
  text = text.replace(/\[(.*?)\]\(.*?\)/g, "$1");
  // Remove bold and italic
  text = text.replace(/(\*\*|__)(.*?)\1/g, "$2"); // bold
  text = text.replace(/(\*|_)(.*?)\1/g, "$2"); // italic
  // Remove headings
  text = text.replace(/^\s{0,3}#{1,6}\s*/gm, "");
  // Remove blockquotes
  text = text.replace(/^\s{0,3}>\s?/gm, "");
  // Remove horizontal rules
  text = text.replace(/^(-\s?){3,}$/gm, "");
  // Remove list markers
  text = text.replace(/^\s*([-*+]|\d+\.)\s+/gm, "");
  // Trim and return
  return text.trim();
}

function formatPreview(preview: string | null): string {
  if (!preview) return "";
  const punctuation = [".", "?", "!"];
  const lastChar = preview.slice(-1);

  if (!punctuation.includes(lastChar) && preview.length > 0) {
    return preview + ".";
  }
  return preview;
}

function formatMessage(message: string, maxLength: number): string {
  if (message.length <= maxLength) return message;
  if (maxLength <= 0) return "";

  const truncated = message.slice(0, maxLength);
  const lastSpace = truncated.lastIndexOf(" ");

  const result = `${truncated.slice(0, lastSpace).trim()}...`;
  if (lastSpace === -1) return truncated.trim();
  return result;
}

const TwitterIcon = ({ icon }: { icon: IconDefinition }) => {
  return (
    <div className='tw-text-[#6D6D6D]'>
      <FontAwesomeIcon className='tw-mr-1' icon={icon} />
      22.4k
    </div>
  );
};

function PreviewContainer({
  children,
  visible,
  className,
}: React.PropsWithChildren<{ visible: boolean; className: string }>) {
  return (
    <Segment
      className={classNames({
        [className]: true,
        "tw-my-0 tw-w-full tw-p-6 tw-shadow-lg ": true,
        "tw-hidden": !visible,
      })}
    >
      {children}
    </Segment>
  );
}

function SMSConversationPreview({ children }: React.PropsWithChildren<{}>) {
  return (
    <div className='tw-flex tw-w-full tw-max-w-sm tw-flex-col tw-gap-2'>
      {children}
      <div className='tw-mt-3 tw-flex  tw-items-center tw-gap-3'>
        <div className='tw-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-[#ebecf0]'>
          <FontAwesomeIcon icon={faPlus} className='tw-mx-auto tw-text-grey' />
        </div>
        <div className='tw-flex tw-h-8 tw-w-full tw-select-none tw-items-center tw-rounded-full tw-p-3 tw-text-grey-light tw-ring-1 tw-ring-grey-light'>
          Text Message
        </div>
      </div>
    </div>
  );
}

SMSConversationPreview.Fade = () => {
  return (
    <div className='tw-pointer-events-none tw-absolute tw-bottom-2/3 tw-left-0 tw-right-0 tw-top-0 tw-z-10 tw-rounded-t-lg tw-bg-gradient-to-b tw-from-white tw-to-transparent'></div>
  );
};

SMSConversationPreview.SMSSenderMessage = ({
  children,
  timestamp,
  highlight = false,
}: React.PropsWithChildren<{ timestamp?: string; highlight?: boolean }>) => {
  return (
    <div className={classNames({ "tw-mt-3 tw-flex tw-flex-col tw-gap-2": timestamp })}>
      <div className='tw-flex tw-w-full tw-justify-center tw-text-sm tw-text-grey'>{timestamp}</div>
      <div className='tw-mr-16 tw-flex'>
        <div
          className={classNames({
            "tw-whitespace-pre-line tw-rounded-3xl tw-rounded-es-lg tw-bg-[#ebecf0] tw-px-5 tw-py-3 tw-text-black": true,
            "tw-shadow-lg tw-ring-2 tw-ring-[#0E85FE]": highlight,
          })}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

SMSConversationPreview.SMSRecipientMessage = ({ children }: React.PropsWithChildren<{}>) => {
  return (
    <div className='tw-flex tw-w-full tw-justify-end'>
      <div className='tw-whitespace-pre-line tw-rounded-3xl tw-rounded-ee-lg tw-bg-[#0E85FE] tw-px-5 tw-py-2 tw-text-white'>
        {children}
      </div>
    </div>
  );
};
